import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

// Eagerly loaded — required immediately for the login page
import commonEn from './en/common.json';
import commonAr from './ar/common.json';
import authEn from './en/auth.json';
import authAr from './ar/auth.json';
// Discovery is used across multiple top-level pages — load eagerly to avoid raw-key flicker
import discoveryEn from './en/discovery.json';
import discoveryAr from './ar/discovery.json';
// Classification is shared by Data Classification + Review Center — load eagerly to avoid
// tab/filter labels flashing English before the lazy namespace resolves.
import classificationEn from './en/classification.json';
import classificationAr from './ar/classification.json';
// Assessment Center nav key is used by Sidebar on every page — must be eager.
import assessmentCenterEn from './en/assessmentCenter.json';
import assessmentCenterAr from './ar/assessmentCenter.json';
// clusteringV2 drives the ROPA import wizard + visual canvas — load eagerly so
// the wizard never flashes raw i18n keys on first render (Spec 030 F15).
import clusteringV2En from './en/clusteringV2.json';
import clusteringV2Ar from './ar/clusteringV2.json';
// F9 (032 consent) — the consent module routes (banner configurator, command
// center, curation page) all assume the consent NS is loaded. With useSuspense:false
// and the lazy loadNamespace() flow there is a render race where deep-linking to
// /consent/banner/configure flashes raw keys (e.g. "consent.banner.configureTitle").
// Eager-load to eliminate the race entirely.
import consentEn from './en/consent.json';
import consentAr from './ar/consent.json';
// notification NS is used by TopBar (bell aria-label) on every page — must be eager
// to avoid the aria-label rendering as a raw key before loadNamespace() resolves.
import notificationEn from './en/notification.json';
import notificationAr from './ar/notification.json';
// ROPA NS: three entry points (ROPAPage, ROPACommandCenter, ROPARecordPage) call
// loadNamespace('ropa') in useEffect — i.e. AFTER first paint — so the page flashes
// raw keys (e.g. "ropa.title") before the lazy bundle resolves. Eager-load to kill the race.
import ropaEn from './en/ropa.json';
import ropaAr from './ar/ropa.json';
// ON-13 license NS: LicenseBanner is mounted in AppLayout (shown on every page)
// so it must be eagerly loaded to prevent raw-key flash on first render.
import licenseEn from './en/license.json';
import licenseAr from './ar/license.json';
// settings NS: SettingsPage calls loadNamespace('settings') in a useEffect — after
// first paint — and react-i18next's useTranslation does not re-render on the
// resulting addResourceBundle() call (it binds to 'languageChanged loaded', not
// 'added'), so a fresh page load can permanently show raw keys ("title", "roles",
// "complianceWindows", ...) instead of flashing briefly. Eager-load to kill the race,
// same fix as ropa/consent/notification above.
import settingsEn from './en/settings.json';
import settingsAr from './ar/settings.json';
// ── THE GROUNDED CITATION NAMESPACE — GENERATED, NEVER HAND-EDITED ──────────
// Every regulatory article number the product renders in copy comes from here.
// A migrated string writes `$t(citations:cite_law_art_20)` instead of the digits
// "20"; this bundle supplies the label, and the label was checked at build time
// against a verbatim binding to a hash-pinned SDAIA PDF
// (`compliance/grounding/i18n_citations.py`, source table
// `citation_tokens.yaml`). Regenerate with
// `cd backend && venv/bin/python -m compliance.grounding.i18n_citations`.
//
// MUST BE EAGER. `$t(citations:…)` is resolved by i18next at render time out of
// the CURRENT language's bundle, so if this namespace has not loaded when a
// migrated string renders, the user sees the raw `$t(...)` text — the same
// lazy-namespace race documented for ropa/consent/settings above, except the
// symptom is a missing legal citation rather than a missing label.
import citationsEn from './generated/citations.en.json';
import citationsAr from './generated/citations.ar.json';
// DSR public subject landing NS: /dsr/subject-link/<token> is the only
// UNAUTHENTICATED, customer-facing page left in the app (the owner landing and
// its `dsr-owner-landing` bundle were retired in 047eee377). It was declared in
// `ns` below but never imported here and never loadNamespace()'d by its page, so
// every label rendered as its raw key ("form.outcomeFoundOption") for external
// recipients. It is an entry point — no earlier page warms the cache — so it
// must be eager.
import dsrSubjectLandingEn from './en/dsr-subject-landing.json';
import dsrSubjectLandingAr from './ar/dsr-subject-landing.json';

// Prefer explicit per-test override key ('i18nextLng') over the app's own
// preference key ('language') so that e2e tests can force a language for a
// single page-load without being overwritten by the LanguageContext sync.
const savedLanguage =
  localStorage.getItem('i18nextLng') ||
  localStorage.getItem('language') ||
  'ar';

i18next.use(initReactI18next).init({
  resources: {
    en: {
      common: commonEn, auth: authEn, discovery: discoveryEn,
      classification: classificationEn, assessmentCenter: assessmentCenterEn,
      clusteringV2: clusteringV2En, consent: consentEn, notification: notificationEn,
      ropa: ropaEn, license: licenseEn, settings: settingsEn,
      citations: citationsEn,
      'dsr-subject-landing': dsrSubjectLandingEn,
    },
    ar: {
      common: commonAr, auth: authAr, discovery: discoveryAr,
      classification: classificationAr, assessmentCenter: assessmentCenterAr,
      clusteringV2: clusteringV2Ar, consent: consentAr, notification: notificationAr,
      ropa: ropaAr, license: licenseAr, settings: settingsAr,
      citations: citationsAr,
      'dsr-subject-landing': dsrSubjectLandingAr,
    },
  },
  lng: savedLanguage,
  fallbackLng: 'en',
  defaultNS: 'common',
  fallbackNS: 'common',
  // Declare all namespaces so i18next knows they exist; they are loaded on demand
  ns: [
    'common',
    'auth',
    'dashboard',
    'audit',
    'users',
    'settings',
    'reports',
    'scanner',
    'pdplAssessment',
    'dpo',
    'ropa',
    'consent',
    'dsr',
    'dsr-subject-landing',
    'breach',
    'notification',
    'risk',
    'dataflow',
    'compliance',
    'classification',
    'discovery',
    'organization',
    'privilegeReview',
    'assessmentCenter',
    'dpta',
    'dpoCuration',
    'dpa',
    'privacyNotices',
    'clusteringV2',
    'license',
    'citations',
  ],
  keySeparator: false,
  interpolation: { escapeValue: false },
  // Interpolate {{ var }} tokens in the default value so lazy-loaded namespaces
  // render correct numbers even on the first paint (before the bundle resolves).
  parseMissingKeyHandler: (key, defaultValue, options) => {
    if (!defaultValue) return key;
    return defaultValue.replace(/\{\{(\w+)\}\}/g, (match, k) =>
      (options && Object.prototype.hasOwnProperty.call(options, k) ? options[k] : match),
    );
  },
  react: { useSuspense: false },
});

// Set initial document direction
document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
document.documentElement.lang = savedLanguage;

/**
 * Dynamically loads a namespace for both languages and registers it with i18next.
 * Safe to call multiple times — i18next merges duplicate bundles (deep merge, override).
 *
 * @param {string} ns - The namespace name matching a JSON file in ./en/ and ./ar/
 * @returns {Promise<void>}
 */
export async function loadNamespace(ns) {
  const [en, ar] = await Promise.all([
    import(`./en/${ns}.json`),
    import(`./ar/${ns}.json`),
  ]);
  i18next.addResourceBundle('en', ns, en.default, true, true);
  i18next.addResourceBundle('ar', ns, ar.default, true, true);
}

export default i18next;
